<script setup>
// Highlight.js
import hljs from 'highlight.js/lib/core'
import php from 'highlight.js/lib/languages/php'
import javascript from 'highlight.js/lib/languages/javascript'
import xml from 'highlight.js/lib/languages/xml'
import { computed, } from 'vue'
import { getActivePinia, } from 'pinia'
import { useSnackbarStore, } from '@/assets/js/src/modules/snackbar/_pinia/snackbar'
import { usePrefetchData, } from '@/assets/js/src/util/composables/usePrefetchData'
import { useTranslation, } from '@/assets/js/src/util/composables'

hljs.registerLanguage('javascript', javascript)
hljs.registerLanguage('php', php)
hljs.registerLanguage('xml', xml)

const props = defineProps({
    language: {
        type: String,
        default: '',
    },
    code: {
        type: String,
        default: '',
    },
})

const highlighted = computed(() => {
    const language = props.language.trim()
    if (hljs.getLanguage(language)) {
        return {
            className: 'hljs ' + language,
            html: hljs.highlight(props.code, { language, ignoreIllegals: true, }).value,
        }
    }

    const result = hljs.highlightAuto(props.code)

    return {
        className: 'hljs ' + (result.language ?? ''),
        html: result.value,
    }
})

let { messagesLoaded, } = usePrefetchData({
    loadAsyncMessages: [ 'webmasters', 'common', ],
})

const { t, } = useTranslation()

const id = 'code-block-' + Date.now() + ''
const snackbarStore = useSnackbarStore(getActivePinia())


const copyToClipboard = function () {
    let element = document.getElementById(id)
    let isReadOnly = element.hasAttribute('readonly')

    if (!isReadOnly) {
        element.setAttribute('readonly', '')
    }
    element.select()
    element.setSelectionRange(0, element.value.length)

    let succeeded

    try {
        succeeded = document.execCommand('copy')
    } catch {
        succeeded = false
    }

    if (isReadOnly) {
        element.removeAttribute('readonly')
    }

    snackbarStore.showMessage({
        message: succeeded ? t('async.webmasters.copy.confirm') : t('async.common.standard_error'),
        type: succeeded ? 'success' : 'error',
    })
}
</script>

<template>
    <section
        class="code-block"
        dir="ltr"
    >
        <pre v-if="language.trim() === 'Text' || language.trim() === ''">
            <code class="text"><!--
                -->{{ code }}<!--
         --></code>
        </pre>
        <pre v-else><code
            :class="highlighted.className"
            tabindex="0"
            v-html="highlighted.html"
        ></code></pre>
        <label
            class="d-sr-only"
            :for="id"
        >
            Code
        </label>
        <input
            :id="id"
            type="text"
            class="d-sr-only"
            :value="code"
            aria-hidden="true"
            tabindex="-1"
        >
        <div class="code-block__copy">
            <span>{{ (language.trim() === 'xml' ? 'html' : language) }}</span>
            <v-btn
                icon
                variant="text"
                color="#fff"
                :title="messagesLoaded ? t('async.webmasters.copy.hint'):''"
                @click="copyToClipboard"
            >
                <v-icon>bs:$vuetify.icons.mdiContentCopy</v-icon>
            </v-btn>
        </div>
    </section>
</template>

<style lang="scss">
.bs-app .code-block {
    position: relative;
    margin-bottom: 16px;

    span {
        color: inherit;
    }

    &__copy {
        position: absolute;
        top: 0;
        right: 0;

        span {
            color: #888;
            font-size: 1rem;
        }
    }

    code {
        display: block;
        width: 100%;
        padding: 1.2em 1.4em;
        overflow-x: auto;
        color: map-deep-get($bs-color, white);
        font-weight: normal;
        font-size: 1rem !important;
        line-height: 1.6rem;
        white-space: pre;
        background: #333;
        box-shadow: none;

        span {
            font-size: 1rem !important;
        }

        p {
            margin: 0;
            color: map-deep-get($bs-color, white);
        }

        h1 {
            color: map-deep-get($bs-color, white);
            font-weight: bold;
            font-size: 1.25rem;
        }

        &::before {
            display: none;
            content: "";
        }

        &::after {
            display: none;
            content: "";
        }
    }

    .hljs {
        display: block;
        margin: 0 0 16px;
        overflow-x: auto;
        color: white;
        background: #333;

        span.hljs {
            &-name, &-strong {
                font-weight: bold;
            }

            &-code, &-emphasis {
                font-style: italic;
            }

            &-tag {
                color: #62c8f3;
            }

            &-variable, &-template-variable, &-selector-id, &-selector-class {
                color: #ade5fc;
            }

            &-string, &-bullet {
                color: #a2fca2;
            }

            &-type, &-title, &-section, &-attribute, &-quote, &-built_in, &-builtin-name {
                color: #ffa;
            }

            &-number, &-symbol, &-bullet {
                color: #d36363;
            }

            &-keyword, &-selector-tag, &-literal {
                color: #fcc28c;
            }

            &-comment, &-deletion, &-code {
                color: #888;
            }

            &-regexp, &-link {
                color: #c6b4f0;
            }

            &-meta {
                color: #fc9b9b;
            }

            &-deletion {
                color: #333;
                background-color: #fc9b9b;
            }

            &-addition {
                color: #333;
                background-color: #a2fca2;
            }

            a {
                color: inherit;

                &:focus, &:hover {
                    color: inherit;
                    text-decoration: underline;
                }
            }
        }
    }
}
</style>
